function slugify(text) {
  return String(text || '')
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 64);
}

class TpuArticlePage extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('[data-tpu-article-content]');
    this.toc = this.querySelector('[data-tpu-article-toc]');
    this.tocList = this.querySelector('[data-tpu-article-toc-list]');
    this.mobileToc = this.querySelector('[data-tpu-article-toc-mobile]');
    this.mobileTocList = this.querySelector('[data-tpu-article-toc-list-mobile]');
    this.tocTrigger = this.querySelector('[data-tpu-toc-trigger]');
    this.tocCurrent = this.querySelector('[data-tpu-toc-current]');
    this.tocSentinel = this.querySelector('[data-tpu-toc-sentinel]');
    this.hero = this.querySelector('[data-tpu-article-hero]');
    if (!this.content) return;

    this._tocScrollLock = false;
    this._tocScrollUnlockTimer = null;
    this._isStuck = false;
    this._ignoreDocClickUntil = 0;
    this._mobileMq = window.matchMedia('(max-width: 1023.98px)');
    this._defaultCurrentLabel = this.tocCurrent?.textContent?.trim() || 'In this article';

    this.enhanceFeatureBlocks();
    this.buildToc();
    this.updateSidebarOffset();
    this.initMobileToc();

    this.onScroll = () => {
      this.updateSidebarOffset();
    };
    this.onMobileMqChange = () => {
      if (!this.isMobileToc()) {
        this.setStuck(false);
        this.closeMobileToc();
      } else {
        this.syncStuckFromHero();
      }
    };
    this.onTriggerClick = () => {
      this._ignoreDocClickUntil = Date.now() + 400;
      this.toggleMobileToc();
    };
    this.onKeydown = (event) => {
      if (event.key !== 'Escape') return;
      if (!this.mobileToc?.classList.contains('is-open')) return;
      this.closeMobileToc();
      this.tocTrigger?.focus();
    };
    this.onDocumentClick = (event) => {
      if (!this._isStuck) return;
      if (!this.mobileToc?.classList.contains('is-open')) return;
      if (Date.now() < this._ignoreDocClickUntil) return;
      if (!(event.target instanceof Node)) return;
      if (this.mobileToc.contains(event.target)) return;
      this.closeMobileToc();
    };

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onMobileMqChange, { passive: true });
    document.addEventListener('header-height-change', this.onScroll);
    this._mobileMq.addEventListener?.('change', this.onMobileMqChange);
    this.tocTrigger?.addEventListener('click', this.onTriggerClick);
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);
    this.onScroll();
  }

  disconnectedCallback() {
    if (this.onScroll) {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      document.removeEventListener('header-height-change', this.onScroll);
    }
    window.removeEventListener('resize', this.onMobileMqChange);
    this._mobileMq?.removeEventListener?.('change', this.onMobileMqChange);
    this.tocTrigger?.removeEventListener('click', this.onTriggerClick);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
    if (this._tocObserver) this._tocObserver.disconnect();
    if (this._stuckObserver) this._stuckObserver.disconnect();
    if (this._tocScrollUnlockTimer) window.clearTimeout(this._tocScrollUnlockTimer);
  }

  isMobileToc() {
    return this._mobileMq?.matches ?? false;
  }

  initMobileToc() {
    if (!this.mobileToc || !this.headings?.length) return;

    const observeTarget = this.hero || this.tocSentinel;
    if (!observeTarget || !('IntersectionObserver' in window)) return;

    this._stuckObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry || !this.isMobileToc()) {
          this.setStuck(false);
          return;
        }
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.setStuck(scrolledPast);
      },
      { threshold: 0, rootMargin: '0px' }
    );
    this._stuckObserver.observe(observeTarget);
    this.syncStuckFromHero();
  }

  syncStuckFromHero() {
    if (!this.isMobileToc()) {
      this.setStuck(false);
      return;
    }
    const target = this.hero || this.tocSentinel;
    if (!target) {
      this.setStuck(false);
      return;
    }
    this.setStuck(target.getBoundingClientRect().bottom < 0);
  }

  setStuck(stuck) {
    if (stuck === this._isStuck) return;
    if (!stuck) this.closeMobileToc();
    this._isStuck = stuck;
    this.classList.toggle('is-toc-stuck', stuck);
    this.mobileToc?.classList.toggle('is-stuck', stuck);
    document.documentElement.style.setProperty(
      '--tpu-article-toc-bar-height',
      stuck ? '3rem' : '0px'
    );
  }

  toggleMobileToc() {
    if (!this.mobileToc) return;
    if (this.mobileToc.classList.contains('is-open')) this.closeMobileToc();
    else this.openMobileToc();
  }

  openMobileToc() {
    if (!this.mobileToc) return;
    this.mobileToc.classList.add('is-open');
    this.tocTrigger?.setAttribute('aria-expanded', 'true');
  }

  closeMobileToc() {
    if (!this.mobileToc) return;
    this.mobileToc.classList.remove('is-open');
    this.tocTrigger?.setAttribute('aria-expanded', 'false');
  }

  updateSidebarOffset() {
    const gap = 10;
    let clearance = 0;

    const fixedHeader = document.querySelector('.tpu-header.is-fixed');
    if (fixedHeader) {
      clearance = Math.max(clearance, fixedHeader.getBoundingClientRect().bottom);
    }

    document.querySelectorAll('.topbar-container, .header-group > *').forEach((el) => {
      const style = getComputedStyle(el);
      if (style.position !== 'fixed' && style.position !== 'sticky') return;
      const rect = el.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top > 80) return;
      clearance = Math.max(clearance, rect.bottom);
    });

    if (clearance < 1) {
      const stickyVar = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--tpu-sticky-header-height')
      );
      clearance = Number.isFinite(stickyVar) && stickyVar > 0 ? Math.min(stickyVar, 72) : 48;
    }

    document.documentElement.style.setProperty(
      '--tpu-article-sidebar-top',
      `${Math.round(clearance + gap)}px`
    );
  }

  getHeaderOffset() {
    const styles = getComputedStyle(document.documentElement);
    const sticky = parseFloat(styles.getPropertyValue('--tpu-sticky-header-height'));
    const header = parseFloat(styles.getPropertyValue('--header-height'));
    const tocBar = parseFloat(styles.getPropertyValue('--tpu-article-toc-bar-height'));
    const base =
      Number.isFinite(sticky) && sticky > 0
        ? sticky
        : Number.isFinite(header) && header > 0
          ? header
          : 96;
    const bar = this._isStuck && Number.isFinite(tocBar) ? tocBar : 0;
    return base + bar + 20;
  }

  scrollToHeading(heading, instant = false) {
    if (!heading) return;

    const prefersReduced =
      instant ||
      (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    const top = window.scrollY + heading.getBoundingClientRect().top - this.getHeaderOffset();

    this._tocScrollLock = true;
    if (this._tocScrollUnlockTimer) window.clearTimeout(this._tocScrollUnlockTimer);

    window.scrollTo({
      top: Math.max(0, top),
      behavior: prefersReduced ? 'auto' : 'smooth',
    });

    this._tocScrollUnlockTimer = window.setTimeout(
      () => {
        this._tocScrollLock = false;
      },
      prefersReduced ? 50 : 900
    );
  }

  allTocLinks() {
    return Array.from(
      this.querySelectorAll('[data-tpu-article-toc-list] .tpu-article__toc-link, [data-tpu-article-toc-list-mobile] .tpu-article__toc-link')
    );
  }

  setActiveToc(activeId) {
    const links = this.allTocLinks();
    let label = this._defaultCurrentLabel;

    links.forEach((link) => {
      const on = link.dataset.tocTarget === activeId;
      link.classList.toggle('is-active', on);
      link.setAttribute('aria-current', on ? 'true' : 'false');
      if (on) {
        const text = link.querySelector('.tpu-article__toc-label')?.textContent?.trim();
        if (text) label = text;
      }
    });

    if (this.tocCurrent) this.tocCurrent.textContent = label;
  }

  setupTocObserver() {
    if (!this.headings?.length || !('IntersectionObserver' in window)) return;
    if (this._tocObserver) this._tocObserver.disconnect();

    this._tocObserver = new IntersectionObserver(
      (entries) => {
        if (this._tocScrollLock) return;
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        this.setActiveToc(visible.target.id);
      },
      { rootMargin: '-25% 0px -55% 0px', threshold: [0.05, 0.2, 0.4, 0.6] }
    );

    this.headings.forEach((heading) => this._tocObserver.observe(heading));
    if (this.headings[0]) this.setActiveToc(this.headings[0].id);
  }

  createTocItem(heading, index, used, stepState) {
    let id = heading.id || slugify(heading.textContent) || `section-${index + 1}`;
    if (used.has(id)) id = `${id}-${index + 1}`;
    used.add(id);
    heading.id = id;
    heading.classList.add('tpu-article__heading');

    const li = document.createElement('li');
    const isSub = heading.tagName === 'H3';
    li.className = isSub
      ? 'tpu-article__toc-item tpu-article__toc-item--sub'
      : 'tpu-article__toc-item';

    const link = document.createElement('a');
    link.href = `#${id}`;
    link.className = 'tpu-article__toc-link';
    link.dataset.tocTarget = id;
    link.setAttribute('aria-current', 'false');

    if (!isSub) {
      stepState.value += 1;
      const num = document.createElement('span');
      num.className = 'tpu-article__toc-num';
      num.setAttribute('aria-hidden', 'true');
      num.textContent = String(stepState.value).padStart(2, '0');
      link.appendChild(num);
    } else {
      const mark = document.createElement('span');
      mark.className = 'tpu-article__toc-mark';
      mark.setAttribute('aria-hidden', 'true');
      link.appendChild(mark);
    }

    const label = document.createElement('span');
    label.className = 'tpu-article__toc-label';
    label.textContent = heading.textContent.trim();
    link.appendChild(label);

    li.appendChild(link);
    return { li, id };
  }

  buildToc() {
    this.headings = Array.from(this.content.querySelectorAll('h2, h3')).filter((node) =>
      node.textContent?.trim()
    );
    if (!this.headings.length) return;

    // First pass assigns heading ids once, then clone structure into both lists
    const used = new Set();
    const stepState = { value: 0 };
    const items = this.headings.map((heading, index) =>
      this.createTocItem(heading, index, used, stepState)
    );

    const fill = (listEl) => {
      if (!listEl) return;
      listEl.innerHTML = '';
      items.forEach(({ li }) => listEl.appendChild(li.cloneNode(true)));
      listEl.querySelectorAll('.tpu-article__toc-link').forEach((link) => {
        const id = link.dataset.tocTarget;
        const heading = this.headings.find((h) => h.id === id);
        link.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!heading) return;
          this.setActiveToc(id);
          this.closeMobileToc();
          this.scrollToHeading(heading);
          history.replaceState(null, '', `#${id}`);
        });
      });
    };

    fill(this.tocList);
    fill(this.mobileTocList);

    if (this.toc) this.toc.hidden = false;
    if (this.mobileToc) this.mobileToc.hidden = false;

    this.setupTocObserver();

    if (window.location.hash) {
      const hashId = window.location.hash.slice(1);
      const target = this.headings.find((heading) => heading.id === hashId);
      if (target) {
        this.setActiveToc(hashId);
        requestAnimationFrame(() => this.scrollToHeading(target, true));
      }
    }
  }

  enhanceFeatureBlocks() {
    if (this.content.querySelector('.feature-grid, .tpu-article__feature-grid')) return;

    const nodes = Array.from(this.content.children);
    let run = [];

    const flush = () => {
      if (run.length < 2) {
        run = [];
        return;
      }
      const wrap = document.createElement('div');
      wrap.className = 'tpu-article__feature-grid';
      run[0].parentNode.insertBefore(wrap, run[0]);
      run.forEach((node) => wrap.appendChild(node));
      run = [];
    };

    for (let i = 0; i < nodes.length; i += 1) {
      const node = nodes[i];
      if (node.matches('h3') && nodes[i + 1]?.matches('p')) {
        const pair = document.createElement('div');
        pair.className = 'tpu-article__feature-item';
        const p = nodes[i + 1];
        node.parentNode.insertBefore(pair, node);
        pair.appendChild(node);
        pair.appendChild(p);
        run.push(pair);
        i += 1;
      } else {
        flush();
      }
    }
    flush();
  }
}

if (!customElements.get('tpu-article-page')) {
  customElements.define('tpu-article-page', TpuArticlePage);
}
